import React, { useContext, useEffect, useState } from 'react';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import useFetch from 'use-http';
import { OwnerWhereAttachedDTO } from 'src/models/OwnerWhereAttached';
import Button from '../controls/forms/redesign/FormButton';
import { IoMdDownload } from 'react-icons/io';
import Dialog from 'src/components/common/Dialog.redesign';
import Pager from '../common/table/Pager';
import PagedResultSet from 'src/models/common/PagedResultSet';
import MathUtil from 'src/util/MathUtil';

interface IWhereAttachedDialogProps {
    ownerId: number;
}

const PAGE_SIZE = 25;

const WhereAttachedDialog: React.FC<IWhereAttachedDialogProps> = ({ ownerId }) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get: getData, response: jsonResponse } = useFetch();
    const { get: getExcel } = useFetch({
        headers: {
            Accept: 'application/octet-stream',
        },
        responseType: 'blob',
    });

    const [whereAttachedData, setWhereAttachedData] = useState<PagedResultSet<OwnerWhereAttachedDTO>>();
    const [pageNumber, setPageNumber] = useState(1); // Track page state

    useEffect(() => {
        getWhereAttachedData(ownerId, pageNumber);
    }, [ownerId, pageNumber]);

    const getWhereAttachedData = async (id: number, page: number) => {
        const skip = (page - 1) * PAGE_SIZE;

        const data = await getData(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/${id}/where-attached?$skip=${skip}&$top=${PAGE_SIZE}`
        );

        if (jsonResponse.ok) {
            setWhereAttachedData(data);
        } else {
            console.log('bad request', jsonResponse);
        }
    };

    const downloadWhereAttachedData = async () => {
        try {
            const fileBlob: Blob = await getExcel(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/${ownerId}/where-attached/report`
            );

            if (!fileBlob || fileBlob.type !== 'application/octet-stream') {
                console.log('Unexpected file response:', fileBlob);
                return;
            }

            const url = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `WhereAttached_Owner_${ownerId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log('Download failed:', error);
        }
    };

    return (
        <Dialog title="Where Attached">
            {/* Report button aligned right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: '1rem', marginBottom: '12px' }}>
                <Button
                    reactIcon={IoMdDownload}
                    iconStyle={{ width: '2rem', height: '2rem', verticalAlign: 'middle' }}
                    iconHoverColor="blue"
                    title="Download"
                    style={{ height: '3rem', background: 'transparent' }}
                    onClick={downloadWhereAttachedData}
                />
            </div>

            {/* Scrollable container */}
            <div style={{ width: '80vw', height: '50vh', overflow: 'auto' }}>
                <table style={{ minWidth: '1200px', width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ textAlign: 'left' }}>
                        <tr>
                            <th style={{ width: '4%', verticalAlign: 'top' }}>Type</th>
                            <th style={{ width: '10%', verticalAlign: 'top' }}>Owner Name</th>
                            <th style={{ width: '4%', verticalAlign: 'top' }}>Tract #</th>
                            <th style={{ width: '7%', verticalAlign: 'top' }}>Sec/Twn/Rng</th>
                            <th style={{ width: '15%', verticalAlign: 'top' }}>Legal Description</th>
                            <th style={{ width: '10%', verticalAlign: 'top' }}>County</th>
                            <th style={{ width: '5%', verticalAlign: 'top' }}>Gross Acres</th>
                            <th style={{ width: '5%', verticalAlign: 'top' }}>Net Acres</th>
                            <th style={{ width: '8%', verticalAlign: 'top' }}>Decimal Interest</th>
                            <th style={{ width: '8%', verticalAlign: 'top' }}>Lease #</th>
                            <th style={{ width: '6%', verticalAlign: 'top' }}>Lease Date</th>
                            <th style={{ width: '6%', verticalAlign: 'top' }}>Effective Dates</th>
                            <th style={{ width: '4%', verticalAlign: 'top' }}>Status</th>
                            <th style={{ width: '8%', verticalAlign: 'top' }}>Buyer</th>
                        </tr>
                    </thead>
                    <tbody style={{ marginTop: '18px' }}>
                        {whereAttachedData?.items.map((item, index) => (
                            <tr key={index} className="table-item-hover" style={{ textAlign: 'left', maxHeight: '2rem', overflow: 'hidden' }}>
                                <td style={{ verticalAlign: 'top', alignContent: 'center' }}>{item.ownershipType ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.ownerName ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.tractNumber ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.secTwnRng ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>
                                    <div style={{ maxHeight: '5rem', overflow: 'hidden', paddingBottom: '0.5rem' }} title={item.legalDescription}>{item.legalDescription ?? '—'}</div>
                                </td>
                                <td style={{ verticalAlign: 'top' }}>{item.countyStateName ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{MathUtil.round(item.grossAcres, 7) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{MathUtil.round(item.netAcres, 7) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{MathUtil.round(item.decimalInterest, 10) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.leaseNumber ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.leaseDate?.substring(0, 10) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.effectiveDate?.substring(0, 10) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.leasedStatus ?? '—'}</td>
                                <td style={{ verticalAlign: 'top' }}>{item.leaseBuyerName ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {whereAttachedData && (
                <Pager
                    pageSize={PAGE_SIZE}
                    pageNumber={whereAttachedData.pageNumber}
                    totalRecords={whereAttachedData.totalRecords}
                    onPageChange={(newPage: number) => setPageNumber(newPage)}
                    currentPageCount={whereAttachedData.items.length}
                />
            )}
        </Dialog>
    );
};

export default WhereAttachedDialog;



// WEBPACK FOOTER //
// ./src/components/dialogs/WhereAttachedDialog.tsx